export interface OfficeAutocompleteAddress {
    buildingNumber?: string
    state?: string
    city?: string
    postalCode?: string
    address?: string
    baseAddress?: string
    formattedAddress?: string
    subpremise?: string
    valueEqual?: boolean
}

export type OfficeAutocompleteEvent = OfficeAutocompleteAddress & {
    eventType?: 'selection' | 'input' | 'error'
    inputValue?: string
    errorMessage?: string
}

export const RESTRICTED_OFFICE_REGISTRATION_STATES = ['CA', 'CALIFORNIA', 'AK', 'ALASKA'];

export const normalizeUnitSuite = (unitSuite?: string): string => {
    return (unitSuite || '')
        .trim()
        .replace(/^(?:#|unit\s+|suite\s+)/i, '')
        .trim();
}

export const composeOfficeAddress = (address?: string, unitSuite?: string): string => {
    const baseAddress = (address || '').trim();
    const normalizedUnitSuite = normalizeUnitSuite(unitSuite);

    if (!normalizedUnitSuite) {
        return baseAddress;
    }

    const suffix = `#${normalizedUnitSuite}`;
    if (baseAddress.toLowerCase().endsWith(suffix.toLowerCase())) {
        return baseAddress;
    }

    return `${baseAddress} ${suffix}`.trim();
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getOfficeBaseAddress = (autocomplete: OfficeAutocompleteAddress): string => {
    const storedBaseAddress = (autocomplete.baseAddress || '').trim();
    if (storedBaseAddress) {
        return storedBaseAddress;
    }

    const address = (autocomplete.address || '').trim();
    const previousSubpremise = normalizeUnitSuite(autocomplete.subpremise);
    if (!previousSubpremise) {
        return address;
    }

    const priorSuiteSuffix = new RegExp(
        `(?:\\s*,?\\s*)(?:#|unit\\s+|suite\\s+)${escapeRegExp(previousSubpremise)}\\s*$`,
        'i',
    );

    return address.replace(priorSuiteSuffix, '').trim();
}

export const getOfficeAddressValidationError = (
    addressInput?: string,
    autocomplete?: OfficeAutocompleteAddress,
): string | undefined => {
    if (!(addressInput || '').trim()) {
        return 'Please enter your address';
    }

    if (!autocomplete) {
        return 'Please select your address from the list';
    }

    const normalizedState = (autocomplete.state || '').trim().toUpperCase();
    if (RESTRICTED_OFFICE_REGISTRATION_STATES.includes(normalizedState)) {
        return 'We are not currently operating in California or Alaska. Please select a different address.';
    }

    if (!(autocomplete.buildingNumber || '').trim()) {
        return 'Please select an address that includes a building number and street';
    }

    const baseAddress = (autocomplete.address || '').trim();
    if (!baseAddress) {
        return 'Please select an address that includes a street';
    }

    const buildingNumber = (autocomplete.buildingNumber || '').trim();
    if (baseAddress.startsWith(buildingNumber) && !baseAddress.slice(buildingNumber.length).trim()) {
        return 'Please select an address that includes a street';
    }

    if (!(autocomplete.city || '').trim() || !(autocomplete.state || '').trim() || !(autocomplete.postalCode || '').trim()) {
        return 'Please select an address that includes a city, province/state, and postal/ZIP code';
    }

    return undefined;
}

export const buildCompletedOfficeAddress = (
    autocomplete: OfficeAutocompleteAddress,
    unitSuite?: string,
): OfficeAutocompleteAddress => {
    const normalizedUnitSuite = normalizeUnitSuite(unitSuite);
    const baseAddress = getOfficeBaseAddress(autocomplete);

    return {
        ...autocomplete,
        address: composeOfficeAddress(baseAddress, normalizedUnitSuite),
        baseAddress: baseAddress || undefined,
        subpremise: normalizedUnitSuite || undefined,
    };
}
